import { useCallback, useEffect, useMemo, useState } from 'react';
import { Layout } from '@/components/Layout';
import {
  FileText, Search, Download, Calendar, Car, User, QrCode,
  MessageSquare, PhoneCall, AlertTriangle, ArrowLeftRight, Bell, Plus,
  Edit, Ban, CheckCircle2, UserCog, Settings, ChevronDown, ChevronUp,
  RefreshCw
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { activityLogEndpoints, api, getApiErrorMessage, reportEndpoints } from '@/lib/api';
import { toast } from 'sonner';

const categoryConfig = {
  Todos: { icon: FileText, color: 'text-slate-600', bg: 'bg-slate-50' },
  Veiculos: { icon: Car, color: 'text-blue-600', bg: 'bg-blue-50' },
  Condutores: { icon: User, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  Tags: { icon: QrCode, color: 'text-purple-600', bg: 'bg-purple-50' },
  Ocorrencias: { icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50' },
  SMS: { icon: MessageSquare, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  Chamadas: { icon: PhoneCall, color: 'text-purple-600', bg: 'bg-purple-50' },
  Contactos: { icon: Bell, color: 'text-blue-600', bg: 'bg-blue-50' },
  Sistema: { icon: Settings, color: 'text-slate-600', bg: 'bg-slate-50' },
};

const typeConfig = {
  vehicle_update: { icon: Edit, category: 'Veiculos' },
  vehicle_created: { icon: Plus, category: 'Veiculos' },
  driver_assigned: { icon: UserCog, category: 'Condutores' },
  driver_removed: { icon: UserCog, category: 'Condutores' },
  driver_swap: { icon: ArrowLeftRight, category: 'Condutores' },
  tag_assigned: { icon: QrCode, category: 'Tags' },
  tag_deactivated: { icon: Ban, category: 'Tags' },
  incident_created: { icon: AlertTriangle, category: 'Ocorrencias' },
  incident_resolved: { icon: CheckCircle2, category: 'Ocorrencias' },
  sms_sent: { icon: MessageSquare, category: 'SMS' },
  sms_failed: { icon: MessageSquare, category: 'SMS' },
  call_made: { icon: PhoneCall, category: 'Chamadas' },
  call_no_answer: { icon: PhoneCall, category: 'Chamadas' },
  notification_contact: { icon: Bell, category: 'Contactos' },
  settings_changed: { icon: Settings, category: 'Sistema' },
};

const collectionPayload = (payload, key) => {
  const source = payload?.data ?? payload;
  if (Array.isArray(source)) return source;
  if (Array.isArray(source?.data)) return source.data;
  if (Array.isArray(source?.[key])) return source[key];
  return [];
};

const normalizeKey = (value) => String(value ?? '').toLowerCase().replace(/[-\s.]/g, '_');

const normalizeCategory = (value, type) => {
  const explicit = String(value ?? '').trim();
  const normalized = normalizeKey(explicit);
  const map = {
    resource: 'Veiculos',
    resources: 'Veiculos',
    vehicle: 'Veiculos',
    vehicles: 'Veiculos',
    veiculo: 'Veiculos',
    veiculos: 'Veiculos',
    manager: 'Condutores',
    managers: 'Condutores',
    driver: 'Condutores',
    drivers: 'Condutores',
    condutor: 'Condutores',
    condutores: 'Condutores',
    tag: 'Tags',
    tags: 'Tags',
    occurrence: 'Ocorrencias',
    occurrences: 'Ocorrencias',
    incident: 'Ocorrencias',
    incidents: 'Ocorrencias',
    ocorrencia: 'Ocorrencias',
    ocorrencias: 'Ocorrencias',
    sms: 'SMS',
    call: 'Chamadas',
    calls: 'Chamadas',
    chamada: 'Chamadas',
    chamadas: 'Chamadas',
    contact: 'Contactos',
    contacts: 'Contactos',
    contacto: 'Contactos',
    contactos: 'Contactos',
    system: 'Sistema',
    sistema: 'Sistema',
  };

  return map[normalized] || typeConfig[normalizeKey(type)]?.category || explicit || 'Sistema';
};

const formatTimestamp = (value) => {
  if (!value) return '';
  const normalized = String(value).replace('T', ' ').replace(/\.\d+Z?$/, '');
  return normalized.endsWith('Z') ? normalized.slice(0, -1) : normalized;
};

const normalizeReportEvent = (source = {}, index = 0) => {
  const attributes = source.attributes && typeof source.attributes === 'object' && !Array.isArray(source.attributes)
    ? source.attributes
    : {};
  const event = { ...source, ...attributes };
  const type = normalizeKey(event.type ?? event.event ?? event.action ?? event.operation ?? event.name);
  const category = normalizeCategory(
    event.category ?? event.entity ?? event.entity_type ?? event.subject_type ?? event.model_type,
    type
  );
  const categoryStyle = categoryConfig[category] || categoryConfig.Sistema;
  const typeStyle = typeConfig[type] || {};
  const user = event.user ?? event.actor ?? event.causer ?? {};
  const vehicle = event.vehicle ?? event.resource ?? event.object ?? {};
  const timestamp = formatTimestamp(event.date ?? event.timestamp ?? event.created_at ?? event.logged_at);

  return {
    id: event.id ?? event.uuid ?? `${timestamp || 'report'}-${index}`,
    date: timestamp,
    timestampMs: timestamp ? new Date(timestamp.replace(' ', 'T')).getTime() || 0 : 0,
    type,
    category,
    icon: typeStyle.icon || categoryStyle.icon,
    color: event.color ?? categoryStyle.color,
    bg: event.bg ?? categoryStyle.bg,
    title: event.title ?? event.name ?? event.event_name ?? event.subject ?? event.action_label ?? 'Evento registado',
    description: event.description ?? event.message ?? event.summary ?? event.details ?? event.body ?? '-',
    user: event.user_name ?? event.actor_name ?? event.causer_name ?? user.name ?? user.email ?? 'Sistema',
    vehicle: event.resource_reference ?? event.vehicle_reference ?? event.vehicle_code ?? event.resource_code ?? vehicle.reference ?? vehicle.code ?? vehicle.name ?? '-',
  };
};

const normalizeReportEvents = (payload) => {
  const reports = collectionPayload(payload, 'reports');
  const events = reports.length ? reports : collectionPayload(payload, 'events');
  const logs = events.length ? events : collectionPayload(payload, 'activity_logs');
  return logs.map(normalizeReportEvent);
};

const fetchReportsResponse = async () => {
  try {
    return await api.get(reportEndpoints.list);
  } catch (reportError) {
    if (reportError?.response?.status !== 404) throw reportError;
  }

  try {
    return await api.get(activityLogEndpoints.list);
  } catch (activityLogError) {
    if (activityLogError?.response?.status !== 404) throw activityLogError;
    return api.get(activityLogEndpoints.legacyList);
  }
};

const currentDateKey = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const ReportsPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('Todos');
  const [sortOrder, setSortOrder] = useState('desc');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadReports = useCallback(async ({ showToast = false } = {}) => {
    setLoading(true);
    setError('');

    try {
      const response = await fetchReportsResponse();
      setEvents(normalizeReportEvents(response.data));
      if (showToast) toast.success('Relatorios atualizados');
    } catch (requestError) {
      setEvents([]);
      setError(getApiErrorMessage(requestError, 'Nao foi possivel carregar os relatorios.'));
      if (showToast) toast.error(getApiErrorMessage(requestError, 'Nao foi possivel atualizar os relatorios.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError('');

      try {
        const response = await fetchReportsResponse();
        if (!cancelled) setEvents(normalizeReportEvents(response.data));
      } catch (requestError) {
        if (!cancelled) {
          setEvents([]);
          setError(getApiErrorMessage(requestError, 'Nao foi possivel carregar os relatorios.'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  const categories = useMemo(
    () => ['Todos', ...Array.from(new Set(events.map(event => event.category).filter(Boolean)))],
    [events]
  );

  const filteredEvents = useMemo(() => events
    .filter(event => {
      const query = searchTerm.toLowerCase();
      const matchesSearch =
        String(event.title ?? '').toLowerCase().includes(query) ||
        String(event.description ?? '').toLowerCase().includes(query) ||
        String(event.vehicle ?? '').toLowerCase().includes(query) ||
        String(event.user ?? '').toLowerCase().includes(query);
      const matchesCategory = filterCategory === 'Todos' || event.category === filterCategory;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      const primary = (b.timestampMs || 0) - (a.timestampMs || 0);
      const fallback = String(b.id).localeCompare(String(a.id), undefined, { numeric: true });
      const result = primary || fallback;
      return sortOrder === 'desc' ? result : -result;
    }), [events, filterCategory, searchTerm, sortOrder]);

  const totalEvents = events.length;
  const todayKey = currentDateKey();
  const todayEvents = events.filter(event => String(event.date).startsWith(todayKey)).length;
  const incidentEvents = events.filter(event => event.category === 'Ocorrencias').length;
  const commsEvents = events.filter(event => event.category === 'SMS' || event.category === 'Chamadas').length;

  const exportCsv = () => {
    const headers = ['Data/Hora', 'Categoria', 'Evento', 'Descricao', 'Veiculo', 'Utilizador'];
    const rows = filteredEvents.map(event => [
      event.date,
      event.category,
      event.title,
      event.description,
      event.vehicle,
      event.user,
    ]);
    const escapeCsv = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const csv = [headers, ...rows].map(row => row.map(escapeCsv).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'relatorios.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-slate-900 text-3xl font-secondary font-bold tracking-tight">Relatorios</h1>
            <p className="text-slate-600 font-primary mt-1">Registo completo de todos os acontecimentos do sistema</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={exportCsv} disabled={loading || filteredEvents.length === 0}>
              <Download size={18} className="mr-2" />
              Exportar CSV
            </Button>
            <Button variant="outline" onClick={() => loadReports({ showToast: true })} disabled={loading}>
              <RefreshCw size={18} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'A atualizar...' : 'Atualizar'}
            </Button>
          </div>
        </div>

        {error && (
          <Card className="p-4 border-red-200 bg-red-50 text-sm text-red-700">
            {error}
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-slate-600 text-sm font-medium">Total Eventos</p>
              <FileText className="text-blue-600" size={20} />
            </div>
            <p className="text-2xl font-secondary font-bold text-slate-900">{totalEvents}</p>
            <p className="text-xs text-slate-500 mt-1">Desde o inicio</p>
          </Card>
          <Card className="p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-slate-600 text-sm font-medium">Hoje</p>
              <Calendar className="text-emerald-600" size={20} />
            </div>
            <p className="text-2xl font-secondary font-bold text-slate-900">{todayEvents}</p>
            <p className="text-xs text-slate-500 mt-1">Eventos registados</p>
          </Card>
          <Card className="p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-slate-600 text-sm font-medium">Ocorrencias</p>
              <AlertTriangle className="text-red-600" size={20} />
            </div>
            <p className="text-2xl font-secondary font-bold text-slate-900">{incidentEvents}</p>
            <p className="text-xs text-slate-500 mt-1">Eventos de ocorrencia</p>
          </Card>
          <Card className="p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-slate-600 text-sm font-medium">Comunicacoes</p>
              <MessageSquare className="text-purple-600" size={20} />
            </div>
            <p className="text-2xl font-secondary font-bold text-slate-900">{commsEvents}</p>
            <p className="text-xs text-slate-500 mt-1">SMS + Chamadas</p>
          </Card>
        </div>

        <div className="flex flex-wrap gap-3">
          <div className="flex-1 min-w-[250px] relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
            <Input
              placeholder="Pesquisar eventos, veiculos, utilizadores..."
              className="pl-9"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </div>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {categories.map(category => (
                <SelectItem key={category} value={category}>{category}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}>
            {sortOrder === 'desc' ? <ChevronDown size={16} className="mr-1" /> : <ChevronUp size={16} className="mr-1" />}
            {sortOrder === 'desc' ? 'Mais Recente' : 'Mais Antigo'}
          </Button>
        </div>

        <div className="flex flex-wrap gap-2">
          {categories.map(category => {
            const conf = categoryConfig[category] || categoryConfig.Todos;
            const Icon = conf.icon;
            const count = category === 'Todos' ? events.length : events.filter(event => event.category === category).length;
            const active = filterCategory === category;
            return (
              <Button
                key={category}
                variant={active ? 'default' : 'outline'}
                size="sm"
                className={active ? '' : 'text-slate-600'}
                onClick={() => setFilterCategory(category)}
              >
                <Icon size={14} className="mr-1" />
                {category} ({count})
              </Button>
            );
          })}
        </div>

        <Card>
          <div className="p-4 border-b border-slate-200">
            <h2 className="font-secondary font-bold text-slate-900">
              Registo de Acontecimentos ({filteredEvents.length})
            </h2>
            <p className="text-sm text-slate-600">Todos os eventos do sistema ordenados por data</p>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[170px]">Data/Hora</TableHead>
                <TableHead className="w-[120px]">Categoria</TableHead>
                <TableHead>Evento</TableHead>
                <TableHead>Descricao</TableHead>
                <TableHead className="w-[120px]">Veiculo</TableHead>
                <TableHead className="w-[100px]">Utilizador</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!loading && filteredEvents.map((event) => {
                const Icon = event.icon;
                return (
                  <TableRow key={event.id} className="hover:bg-slate-50">
                    <TableCell className="text-sm text-slate-600 whitespace-nowrap font-mono">{event.date || '-'}</TableCell>
                    <TableCell>
                      <Badge className={`${event.bg} ${event.color} border-0`}>
                        <Icon size={12} className="mr-1" />
                        {event.category}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium text-slate-900">{event.title}</TableCell>
                    <TableCell className="text-sm text-slate-600 max-w-[350px]">{event.description}</TableCell>
                    <TableCell>
                      {event.vehicle !== '-' ? <Badge variant="outline" className="font-mono">{event.vehicle}</Badge> : <span className="text-slate-400">-</span>}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{event.user}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {loading ? (
            <div className="p-12 text-center text-slate-500">
              <RefreshCw className="mx-auto mb-3 text-slate-300 animate-spin" size={40} />
              <p className="font-medium">A carregar relatorios...</p>
            </div>
          ) : filteredEvents.length === 0 && (
            <div className="p-12 text-center text-slate-500">
              <FileText className="mx-auto mb-3 text-slate-300" size={48} />
              <p className="font-medium">Nenhum evento encontrado</p>
              <p className="text-sm">Tente ajustar os filtros de pesquisa</p>
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
};
